import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  VStack,
  HStack,
  Badge,
  Icon,
  Spinner,
  useColorModeValue,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Tooltip,
  Button,
  Collapse,
  Tag,
  TagLabel,
  TagLeftIcon,
  IconButton,
  Link,
  useDisclosure,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  FaSearch,
  FaStar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaChevronDown,
  FaChevronUp,
  FaThumbsUp,
  FaThumbsDown,
  FaMinus,
  FaTag,
  FaLightbulb,
  FaStore,
  FaCrown,
  FaHandHoldingUsd,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaQuoteLeft,
  FaInfoCircle,
  FaGem,
} from 'react-icons/fa';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
  ReferenceLine,
} from 'recharts';
import AnimatedBox from '../AnimatedBox';
import { SectionLabel } from './DashboardWrapper';
import {
  getPricingDashboard,
  highlightKeywordInContext,
  getSentimentDisplay,
} from '../../services/pricingService';
import { logger } from '../../utils/logger';

const TIER_CONFIG = {
  4: { label: '$$$$', title: 'Premium', color: 'orange', hex: '#DD6B20', icon: FaCrown },
  3: { label: '$$$', title: 'Upscale', color: 'purple', hex: '#805AD5', icon: FaGem },
  2: { label: '$$', title: 'Moderate', color: 'blue', hex: '#3182CE', icon: FaStore },
  1: { label: '$', title: 'Budget', color: 'green', hex: '#38A169', icon: FaHandHoldingUsd },
};

const getValueScore = (business) => {
  if (!business.rating_avg) return 0;
  const ratingFactor = (business.rating_avg / 5) * 100;
  const priceFactor = ((5 - business.price_level) / 4) * 100;
  return Math.round(ratingFactor * 0.6 + priceFactor * 0.4);
};

const getValueLabel = (score) => {
  if (score >= 80) return { label: 'Exceptional', color: 'green', icon: FaCheckCircle };
  if (score >= 65) return { label: 'Great', color: 'teal', icon: FaThumbsUp };
  if (score >= 50) return { label: 'Fair', color: 'blue', icon: FaMinus };
  if (score >= 35) return { label: 'Above Market', color: 'orange', icon: FaArrowUp };
  return { label: 'Premium', color: 'red', icon: FaCrown };
};


// ─── Podium Step ──────────────────────────────────────────────────
const PodiumStep = ({ business, place, height, color, delay }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const cfg = business ? (TIER_CONFIG[business.price_level] || TIER_CONFIG[2]) : null;

  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

  if (!business) {
    return (
      <Flex direction="column" align="center" justify="flex-end" flex={1}>
        <Box h={`${height}px`} w="full" bg="gray.100" borderTopRadius="xl" />
      </Flex>
    );
  }

  return (
    <AnimatedBox animation="slideUp" delay={delay} duration={0.45} flex={1} display="flex" flexDirection="column" justifyContent="flex-end" alignItems="center">
      <VStack spacing={1.5} mb={3} maxW="full" px={2}>
        <Text fontSize={place === 1 ? '2xl' : 'xl'} lineHeight="1">{medals[place]}</Text>
        <Text
          fontSize={place === 1 ? 'sm' : 'xs'}
          fontWeight="700"
          color={textColor}
          noOfLines={2}
          textAlign="center"
          lineHeight="1.3"
        >
          {business.name}
        </Text>
        <Badge
          colorScheme={cfg.color}
          variant="subtle"
          fontSize="xs"
          fontWeight="800"
          px={2}
          py={0.5}
          borderRadius="full"
        >
          {cfg.label}
        </Badge>
        {business.rating_avg && (
          <HStack spacing={1}>
            <Icon as={FaStar} color="yellow.400" boxSize={3} />
            <Text fontSize="xs" fontWeight="600" color={muted}>
              {business.rating_avg.toFixed(1)}
            </Text>
          </HStack>
        )}
      </VStack>
      <Box
        h={`${height}px`}
        w="full"
        bgGradient={`linear(to-t, ${color}.500, ${color}.300)`}
        borderTopRadius="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60%',
          height: '4px',
          borderBottomRadius: 'full',
          bg: 'whiteAlpha.400',
        }}
      >
        <Text fontSize={place === 1 ? '3xl' : '2xl'} fontWeight="900" color="white" opacity={0.85}>
          {place}
        </Text>
      </Box>
    </AnimatedBox>
  );
};

// ─── Pricing Podiums: top 3 expensive + top 3 cheapest ────────────
const PricingPodiums = ({ businesses }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const topExpensive = useMemo(() => {
    if (!businesses?.length) return [];
    return [...businesses]
      .sort((a, b) => b.price_level - a.price_level || (b.rating_avg || 0) - (a.rating_avg || 0))
      .slice(0, 3);
  }, [businesses]);

  const topCheapest = useMemo(() => {
    if (!businesses?.length) return [];
    return [...businesses]
      .sort((a, b) => a.price_level - b.price_level || (b.rating_avg || 0) - (a.rating_avg || 0))
      .slice(0, 3);
  }, [businesses]);

  const podiumHeights = { 1: 120, 2: 88, 3: 62 };

  const renderPodium = (items, color, baseDelay) => (
    <Flex align="flex-end" gap={3} h="260px" px={3} pt={2}>
      <PodiumStep business={items[1]} place={2} height={podiumHeights[2]} color={color} delay={baseDelay + 0.1} />
      <PodiumStep business={items[0]} place={1} height={podiumHeights[1]} color={color} delay={baseDelay} />
      <PodiumStep business={items[2]} place={3} height={podiumHeights[3]} color={color} delay={baseDelay + 0.18} />
    </Flex>
  );

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        shadow="sm"
        _hover={{ shadow: 'md' }}
        transition="shadow 0.2s"
      >
        <Box h="4px" bgGradient="linear(to-r, orange.400, red.400)" />
        <CardBody pt={5} pb={4} px={5}>
          <VStack spacing={0.5} mb={3}>
            <HStack spacing={2} justify="center">
              <Icon as={FaCrown} color="orange.400" boxSize={4} />
              <Text fontWeight="800" fontSize="lg" color={textColor} letterSpacing="-0.01em">
                Most Expensive
              </Text>
            </HStack>
            <Text fontSize="xs" color={muted} fontWeight="500">
              Highest priced in your market
            </Text>
          </VStack>
          {renderPodium(topExpensive, 'orange', 0.06)}
        </CardBody>
      </Card>

      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        shadow="sm"
        _hover={{ shadow: 'md' }}
        transition="shadow 0.2s"
      >
        <Box h="4px" bgGradient="linear(to-r, green.400, teal.400)" />
        <CardBody pt={5} pb={4} px={5}>
          <VStack spacing={0.5} mb={3}>
            <HStack spacing={2} justify="center">
              <Icon as={FaHandHoldingUsd} color="green.400" boxSize={4} />
              <Text fontWeight="800" fontSize="lg" color={textColor} letterSpacing="-0.01em">
                Most Affordable
              </Text>
            </HStack>
            <Text fontSize="xs" color={muted} fontWeight="500">
              Best budget options available
            </Text>
          </VStack>
          {renderPodium(topCheapest, 'green', 0.12)}
        </CardBody>
      </Card>
    </SimpleGrid>
  );
};


// ─── Tier Spectrum: horizontal bar + compact legend ───────────────
const TierSpectrum = ({ tierDistribution, totalBusinesses, onTierClick, activeTier, avgRatingByTier, avgReviewsByTier }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const tiers = [4, 3, 2, 1];

  return (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
      <CardBody p={5}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="700" fontSize="md" color={textColor}>
            Price Tier Breakdown
          </Text>
          <Text fontSize="2xs" color={muted}>
            {activeTier ? 'Click again to clear filter' : 'Click a segment to filter'}
          </Text>
        </Flex>

        <Flex h="40px" borderRadius="lg" overflow="hidden" mb={5}>
          {tiers.map((level) => {
            const cfg = TIER_CONFIG[level];
            const count = tierDistribution?.[cfg.label] || 0;
            const pct = totalBusinesses > 0 ? (count / totalBusinesses) * 100 : 0;
            if (pct === 0) return null;

            const isActive = activeTier === String(level);
            const isDimmed = activeTier && !isActive;

            return (
              <Tooltip
                key={level}
                label={`${cfg.title} (${cfg.label}): ${count} businesses — ${pct.toFixed(0)}%`}
                placement="top"
                hasArrow
              >
                <Flex
                  h="100%"
                  w={`${pct}%`}
                  bg={`${cfg.color}.${isDimmed ? '200' : '400'}`}
                  cursor="pointer"
                  onClick={() => onTierClick(level)}
                  transition="all 0.2s"
                  _hover={{ filter: 'brightness(1.1)' }}
                  align="center"
                  justify="center"
                  minW="20px"
                >
                  {pct > 14 && (
                    <Text fontSize="xs" fontWeight="700" color="white">
                      {cfg.label}
                    </Text>
                  )}
                </Flex>
              </Tooltip>
            );
          })}
        </Flex>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          {tiers.map((level) => {
            const cfg = TIER_CONFIG[level];
            const count = tierDistribution?.[cfg.label] || 0;
            const pct = totalBusinesses > 0 ? ((count / totalBusinesses) * 100).toFixed(0) : 0;
            const isActive = activeTier === String(level);
            const avgRating = avgRatingByTier?.[cfg.label];
            const avgReviews = avgReviewsByTier?.[cfg.label];

            return (
              <Box
                key={level}
                p={3}
                borderRadius="lg"
                borderWidth="2px"
                borderColor={isActive ? `${cfg.color}.400` : borderColor}
                bg={isActive ? `${cfg.color}.50` : cardBg}
                cursor="pointer"
                onClick={() => onTierClick(level)}
                transition="all 0.2s"
                _hover={{ borderColor: `${cfg.color}.300` }}
                opacity={activeTier && !isActive ? 0.55 : 1}
              >
                <HStack justify="space-between" mb={1}>
                  <Text fontWeight="800" fontSize="md" color={`${cfg.color}.600`}>
                    {cfg.label}
                  </Text>
                  <Badge colorScheme={cfg.color} variant="subtle" fontSize="2xs" borderRadius="md">
                    {cfg.title}
                  </Badge>
                </HStack>
                <Text fontSize="2xl" fontWeight="800" color={textColor} lineHeight="1.1">
                  {count}
                  <Text as="span" fontSize="xs" fontWeight="500" color={muted} ml={1}>
                    ({pct}%)
                  </Text>
                </Text>
                {(avgRating > 0 || avgReviews > 0) && (
                  <HStack mt={2} spacing={3} fontSize="2xs" color={muted}>
                    {avgRating > 0 && (
                      <HStack spacing={0.5}>
                        <Icon as={FaStar} color="yellow.400" boxSize={2.5} />
                        <Text>{avgRating.toFixed(1)} avg</Text>
                      </HStack>
                    )}
                    {avgReviews > 0 && (
                      <Text>{avgReviews.toLocaleString()} avg reviews</Text>
                    )}
                  </HStack>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      </CardBody>
    </Card>
  );
};


// ─── Value Matrix: scatter chart ──────────────────────────────────
const ValueMatrix = ({ businesses }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const gridStroke = useColorModeValue('#E2E8F0', '#4A5568');
  const tooltipBg = useColorModeValue('white', 'gray.700');
  const refLineStroke = useColorModeValue('#CBD5E0', '#4A5568');

  const scatterData = useMemo(() => {
    if (!businesses) return [];
    return businesses
      .filter(b => b.price_level && b.rating_avg != null && b.rating_avg > 0)
      .map(b => ({
        name: b.name,
        price_level: b.price_level,
        rating: b.rating_avg,
        reviews: b.rating_count || 10,
        tier: b.price_level,
        value_score: getValueScore(b),
      }));
  }, [businesses]);

  const avgRating = useMemo(() => {
    const rated = scatterData.filter(d => d.rating);
    return rated.length ? rated.reduce((s, d) => s + d.rating, 0) / rated.length : 4;
  }, [scatterData]);

  const avgPrice = useMemo(() => {
    return scatterData.length ? scatterData.reduce((s, d) => s + d.price_level, 0) / scatterData.length : 2.5;
  }, [scatterData]);

  return (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
      <CardBody p={5}>
        <VStack align="start" spacing={1} mb={3}>
          <Text fontWeight="700" fontSize="md" color={textColor}>
            Price vs. Quality
          </Text>
          <Text fontSize="xs" color={muted}>
            Top-left = best value (high rating, low price). Bottom-right = overpriced.
          </Text>
        </VStack>

        <HStack spacing={4} mb={2} fontSize="2xs" color={muted} justify="center" flexWrap="wrap">
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="green.400" /> <Text>$ Budget</Text></HStack>
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="blue.400" /> <Text>$$ Moderate</Text></HStack>
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="purple.400" /> <Text>$$$ Upscale</Text></HStack>
          <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="orange.400" /> <Text>$$$$ Premium</Text></HStack>
        </HStack>

        <Box h="320px">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                type="number"
                dataKey="price_level"
                name="Price"
                domain={[0.5, 4.5]}
                ticks={[1, 2, 3, 4]}
                tickFormatter={(v) => '$'.repeat(v)}
                label={{ value: 'Price Level', position: 'bottom', offset: 0, fontSize: 11, fill: muted }}
              />
              <YAxis
                type="number"
                dataKey="rating"
                name="Rating"
                domain={[2.5, 5.2]}
                ticks={[3, 3.5, 4, 4.5, 5]}
                label={{ value: 'Rating', angle: -90, position: 'insideLeft', offset: 10, fontSize: 11, fill: muted }}
              />
              <ZAxis type="number" dataKey="reviews" range={[50, 400]} />
              <ReferenceLine x={avgPrice} stroke={refLineStroke} strokeDasharray="6 4" />
              <ReferenceLine y={avgRating} stroke={refLineStroke} strokeDasharray="6 4" />
              <RechartsTooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    const vl = getValueLabel(d.value_score);
                    return (
                      <Box bg={tooltipBg} p={3} borderRadius="lg" shadow="lg" borderWidth="1px" maxW="220px">
                        <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{d.name}</Text>
                        <Divider my={1} />
                        <HStack fontSize="xs" justify="space-between">
                          <Text color={muted}>Price</Text>
                          <Text fontWeight="600">{'$'.repeat(d.price_level)}</Text>
                        </HStack>
                        <HStack fontSize="xs" justify="space-between" mt={0.5}>
                          <Text color={muted}>Rating</Text>
                          <Text fontWeight="600">{d.rating.toFixed(1)}★</Text>
                        </HStack>
                        <HStack fontSize="xs" justify="space-between" mt={0.5}>
                          <Text color={muted}>Reviews</Text>
                          <Text fontWeight="600">{d.reviews.toLocaleString()}</Text>
                        </HStack>
                        <Tag size="sm" mt={2} colorScheme={vl.color} variant="subtle">
                          <TagLeftIcon as={vl.icon} />
                          <TagLabel>{vl.label} Value</TagLabel>
                        </Tag>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Scatter data={scatterData}>
                {scatterData.map((entry, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={TIER_CONFIG[entry.tier]?.hex || '#718096'}
                    fillOpacity={0.7}
                    stroke={TIER_CONFIG[entry.tier]?.hex || '#718096'}
                    strokeWidth={1}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      </CardBody>
    </Card>
  );
};


// ─── Business Row ─────────────────────────────────────────────────
const BusinessRow = ({ business, rank, isExpanded, onToggle }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');
  const mentionBg = useColorModeValue('gray.50', 'gray.700');

  const cfg = TIER_CONFIG[business.price_level] || TIER_CONFIG[2];
  const valueScore = getValueScore(business);
  const vl = getValueLabel(valueScore);

  const priceVsMarket = useMemo(() => {
    const pvs = business.price_vs_industry || 'average';
    if (pvs === 'below') return { label: 'Below avg', color: 'green', icon: FaArrowDown };
    if (pvs === 'above') return { label: 'Above avg', color: 'orange', icon: FaArrowUp };
    return { label: 'At avg', color: 'gray', icon: FaMinus };
  }, [business.price_vs_industry]);

  const hasEvidence = business.mentions?.length > 0 || business.top_positive_keywords?.length > 0 || business.classification;

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={isExpanded ? `${cfg.color}.200` : borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ borderColor: `${cfg.color}.200` }}
      mb={2}
    >
      <Flex
        p={{ base: 3, md: 4 }}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: hoverBg }}
        transition="background 0.15s"
        align="center"
        gap={{ base: 2, md: 4 }}
      >
        {/* Rank badge */}
        <Flex
          w="36px"
          h="36px"
          borderRadius="lg"
          bg={`${cfg.color}.50`}
          justify="center"
          align="center"
          flexShrink={0}
        >
          <Text fontSize="sm" fontWeight="800" color={`${cfg.color}.600`}>
            {rank}
          </Text>
        </Flex>

        {/* Name + tier */}
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontWeight="600" fontSize="sm" color={textColor} noOfLines={1}>
            {business.name}
          </Text>
          <HStack spacing={2} mt={0.5}>
            <Text fontSize="xs" fontWeight="700" color={`${cfg.color}.500`}>
              {cfg.label}
            </Text>
            <Text fontSize="2xs" color={muted}>{cfg.title}</Text>
          </HStack>
        </VStack>

        {/* Desktop metrics */}
        <HStack spacing={6} flexShrink={0} display={{ base: 'none', md: 'flex' }}>
          {/* vs Market */}
          <VStack spacing={0} align="center" minW="60px">
            <HStack spacing={1}>
              <Icon as={priceVsMarket.icon} color={`${priceVsMarket.color}.400`} boxSize={2.5} />
              <Text fontSize="xs" fontWeight="600" color={`${priceVsMarket.color}.500`}>
                {priceVsMarket.label}
              </Text>
            </HStack>
            <Text fontSize="2xs" color={muted}>vs market</Text>
          </VStack>

          {/* Rating */}
          {business.rating_avg && (
            <VStack spacing={0} align="center" minW="50px">
              <HStack spacing={0.5}>
                <Icon as={FaStar} color="yellow.400" boxSize={3} />
                <Text fontWeight="700" fontSize="sm" color={textColor}>
                  {business.rating_avg.toFixed(1)}
                </Text>
              </HStack>
              <Text fontSize="2xs" color={muted}>
                {business.rating_count?.toLocaleString() || 0}
              </Text>
            </VStack>
          )}

          {/* Value score */}
          <VStack spacing={0} align="center" minW="56px">
            <HStack spacing={1}>
              <Box
                w="28px"
                h="28px"
                borderRadius="full"
                borderWidth="3px"
                borderColor={`${vl.color}.300`}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="2xs" fontWeight="800" color={`${vl.color}.600`}>
                  {valueScore}
                </Text>
              </Box>
            </HStack>
            <Text fontSize="2xs" color={muted}>{vl.label}</Text>
          </VStack>
        </HStack>

        {/* Mobile metrics */}
        <VStack spacing={0} display={{ base: 'flex', md: 'none' }} flexShrink={0} align="end">
          {business.rating_avg && (
            <HStack spacing={0.5}>
              <Icon as={FaStar} color="yellow.400" boxSize={3} />
              <Text fontWeight="700" fontSize="sm" color={textColor}>
                {business.rating_avg.toFixed(1)}
              </Text>
            </HStack>
          )}
          <Badge colorScheme={vl.color} variant="subtle" fontSize="2xs">
            {valueScore} value
          </Badge>
        </VStack>

        <IconButton
          icon={<Icon as={isExpanded ? FaChevronUp : FaChevronDown} />}
          variant="ghost"
          size="xs"
          color={muted}
          aria-label="Expand"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
        />
      </Flex>

      {/* Expanded details */}
      <Collapse in={isExpanded} animateOpacity>
        <Box borderTopWidth="1px" borderColor={borderColor} p={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={hasEvidence ? 4 : 0}>
            {/* Left: key metrics */}
            <VStack align="stretch" spacing={2}>
              <MetricRow label="Price Level" value={`${cfg.label} (${business.price_level}/4)`} badge={cfg.color} />
              <MetricRow label="vs Market" value={priceVsMarket.label} badge={priceVsMarket.color} />
              <MetricRow label="Value Score" value={`${valueScore}/100 — ${vl.label}`} badge={vl.color} />
              <MetricRow
                label="Data Source"
                value={
                  business.price_source === 'google_places' ? 'Google Places'
                    : business.price_source === 'perception' ? 'Review Analysis'
                    : 'Estimated'
                }
              />
              {business.pricing_score !== undefined && (
                <MetricRow label="Perception" value={`${business.pricing_score.toFixed(0)}/100`} />
              )}
              {business.pricing_sentiment && (
                <MetricRow
                  label="Sentiment"
                  value={business.pricing_sentiment === 'positive' ? 'Good value' : business.pricing_sentiment === 'negative' ? 'Pricey' : 'Mixed'}
                  badge={business.pricing_sentiment === 'positive' ? 'green' : business.pricing_sentiment === 'negative' ? 'red' : 'gray'}
                />
              )}
            </VStack>

            {/* Right: business info */}
            <VStack align="stretch" spacing={2}>
              {business.rating_avg && (
                <MetricRow
                  label="Rating"
                  value={
                    <HStack spacing={1}>
                      <Icon as={FaStar} color="yellow.400" boxSize={3} />
                      <Text fontSize="sm" fontWeight="600">{business.rating_avg.toFixed(1)}</Text>
                      <Text fontSize="xs" color={muted}>({business.rating_count?.toLocaleString() || 0} reviews)</Text>
                    </HStack>
                  }
                />
              )}
              {business.primary_type && (
                <MetricRow label="Category" value={business.primary_type.replace(/_/g, ' ')} />
              )}
              {business.address && (
                <HStack fontSize="xs" spacing={1}>
                  <Icon as={FaMapMarkerAlt} color="red.400" boxSize={3} flexShrink={0} />
                  <Text color={muted} noOfLines={1}>{business.address}</Text>
                </HStack>
              )}
              {business.website_uri && (
                <HStack fontSize="xs" spacing={1}>
                  <Icon as={FaExternalLinkAlt} color="blue.400" boxSize={3} flexShrink={0} />
                  <Link
                    href={business.website_uri}
                    isExternal
                    color="blue.500"
                    _hover={{ textDecoration: 'underline' }}
                    noOfLines={1}
                  >
                    Visit website <ExternalLinkIcon mx="2px" boxSize={2.5} />
                  </Link>
                </HStack>
              )}
              {(business.pricing_mentions_positive > 0 || business.pricing_mentions_negative > 0) && (
                <HStack spacing={3} fontSize="xs">
                  <HStack spacing={1}>
                    <Icon as={FaThumbsUp} color="green.400" boxSize={3} />
                    <Text color={muted}>{business.pricing_mentions_positive || 0} positive</Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Icon as={FaThumbsDown} color="red.400" boxSize={3} />
                    <Text color={muted}>{business.pricing_mentions_negative || 0} negative</Text>
                  </HStack>
                </HStack>
              )}
            </VStack>
          </SimpleGrid>

          {/* Evidence section */}
          {hasEvidence && (
            <>
              <Divider my={3} />
              <VStack align="stretch" spacing={3}>
                {/* Keywords */}
                {(business.top_positive_keywords?.length > 0 || business.top_negative_keywords?.length > 0) && (
                  <HStack spacing={2} flexWrap="wrap">
                    {business.top_positive_keywords?.slice(0, 3).map((kw, idx) => (
                      <Tag key={`p-${idx}`} size="sm" colorScheme="green" variant="subtle" borderRadius="full">
                        <TagLabel>&ldquo;{kw.keyword}&rdquo; ({kw.count})</TagLabel>
                      </Tag>
                    ))}
                    {business.top_negative_keywords?.slice(0, 3).map((kw, idx) => (
                      <Tag key={`n-${idx}`} size="sm" colorScheme="red" variant="subtle" borderRadius="full">
                        <TagLabel>&ldquo;{kw.keyword}&rdquo; ({kw.count})</TagLabel>
                      </Tag>
                    ))}
                  </HStack>
                )}

                {/* Review excerpts */}
                {business.mentions?.filter(m => m.reviewer_name).slice(0, 2).map((mention, idx) => {
                  const sentimentInfo = getSentimentDisplay(mention.sentiment);
                  const parts = highlightKeywordInContext(mention.context, mention.keyword);
                  return (
                    <Box
                      key={idx}
                      p={3}
                      borderRadius="md"
                      bg={mentionBg}
                      borderLeftWidth="3px"
                      borderLeftColor={`${sentimentInfo.color}.400`}
                    >
                      <HStack justify="space-between" mb={1} flexWrap="wrap" spacing={2}>
                        <HStack spacing={2}>
                          <Icon as={FaQuoteLeft} boxSize={2.5} color="gray.300" />
                          {mention.review_url ? (
                            <Link href={mention.review_url} isExternal fontSize="xs" fontWeight="500" color="blue.500">
                              {mention.reviewer_name} <ExternalLinkIcon mx="1px" boxSize={2} />
                            </Link>
                          ) : (
                            <Text fontSize="xs" fontWeight="500" color={muted}>{mention.reviewer_name}</Text>
                          )}
                          {mention.keyword && (
                            <Tag size="xs" variant="outline" colorScheme="blue" borderRadius="full">
                              <TagLeftIcon as={FaTag} />
                              <TagLabel>{mention.keyword}</TagLabel>
                            </Tag>
                          )}
                        </HStack>
                        {mention.stars > 0 && (
                          <HStack spacing={0.5}>
                            {[...Array(5)].map((_, i) => (
                              <Icon key={i} as={FaStar} boxSize={2.5} color={i < mention.stars ? 'yellow.400' : 'gray.200'} />
                            ))}
                          </HStack>
                        )}
                      </HStack>
                      <Text fontSize="xs" fontStyle="italic" color={textColor}>
                        &ldquo;...{parts.before}
                        <Text as="span" bg="yellow.100" px={0.5} fontWeight="bold" borderRadius="sm">
                          {parts.keyword}
                        </Text>
                        {parts.after}...&rdquo;
                      </Text>
                    </Box>
                  );
                })}

                {/* Classification insight */}
                {business.classification && (
                  <Box p={3} borderRadius="md" bg="purple.50" borderLeftWidth="3px" borderLeftColor="purple.400">
                    <HStack spacing={1} mb={1}>
                      <Icon as={FaLightbulb} color="purple.500" boxSize={3} />
                      <Text fontSize="xs" fontWeight="600" color="purple.600">{business.classification}</Text>
                    </HStack>
                    {business.insight && <Text fontSize="xs" color={textColor}>{business.insight}</Text>}
                    {business.recommendation && (
                      <Text fontSize="xs" color={muted} mt={1} fontStyle="italic">Tip: {business.recommendation}</Text>
                    )}
                  </Box>
                )}
              </VStack>
            </>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

const MetricRow = ({ label, value, badge }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  return (
    <HStack justify="space-between" w="full" fontSize="xs">
      <Text color={muted}>{label}</Text>
      {typeof value === 'string' ? (
        badge ? (
          <Badge colorScheme={badge} variant="subtle" fontSize="2xs" borderRadius="md" px={2}>
            {value}
          </Badge>
        ) : (
          <Text fontWeight="500" color={textColor}>{value}</Text>
        )
      ) : (
        value
      )}
    </HStack>
  );
};


// ─── Methodology (collapsible) ────────────────────────────────────
const MethodologyNote = () => {
  const { isOpen, onToggle } = useDisclosure();
  const muted = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box mt={6}>
      <Button
        variant="ghost"
        size="xs"
        color={muted}
        leftIcon={<Icon as={FaInfoCircle} />}
        rightIcon={<Icon as={isOpen ? FaChevronUp : FaChevronDown} boxSize={2.5} />}
        onClick={onToggle}
        fontWeight="500"
      >
        How pricing is calculated
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box mt={2} p={4} bg={bg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} fontSize="xs" color={muted}>
            <Box>
              <Text fontWeight="600" color="green.600" mb={1}>Data Collection</Text>
              <Text>Price levels from Google Places API, supplemented with review sentiment analysis using 50+ pricing keywords.</Text>
            </Box>
            <Box>
              <Text fontWeight="600" color="blue.600" mb={1}>Value Scoring</Text>
              <Text>0-100 score: rating (60% weight) vs price tier (40%). Higher rating + lower price = better value.</Text>
            </Box>
            <Box>
              <Text fontWeight="600" color="purple.600" mb={1}>Market Position</Text>
              <Text>Businesses compared against market averages. Sentiment from reviews confirms positioning.</Text>
            </Box>
          </SimpleGrid>
          <Text fontSize="2xs" color={muted} mt={3}>
            80-100 Exceptional · 65-79 Great · 50-64 Fair · 35-49 Above Market · 0-34 Premium
          </Text>
        </Box>
      </Collapse>
    </Box>
  );
};


// ═══════════════════════════════════════════════════════════════════
// MAIN: PricingDashboard
// ═══════════════════════════════════════════════════════════════════
const PricingDashboard = ({ demoMode, demoData } = {}) => {
  const [pricingData, setPricingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [sortBy, setSortBy] = useState('price_desc');
  const [expandedCards, setExpandedCards] = useState({});

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    const fetchData = async () => {
      if (demoMode && demoData) {
        setPricingData(demoData.pricingData || null);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        logger.log('Fetching pricing dashboard data...');
        const data = await getPricingDashboard();
        setPricingData(data);
        logger.log('Pricing data loaded:', data);
      } catch (err) {
        logger.error('Error fetching pricing data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [demoMode, demoData]);

  const filteredBusinesses = useMemo(() => {
    if (!pricingData?.businesses) return [];

    let filtered = [...pricingData.businesses];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.name.toLowerCase().includes(q) ||
        b.primary_type?.toLowerCase().includes(q) ||
        b.address?.toLowerCase().includes(q)
      );
    }

    if (filterTier !== 'all') {
      const level = parseInt(filterTier);
      filtered = filtered.filter(b => b.price_level === level);
    }

    switch (sortBy) {
      case 'price_desc':
        filtered.sort((a, b) => b.price_level - a.price_level || (b.rating_avg || 0) - (a.rating_avg || 0));
        break;
      case 'price_asc':
        filtered.sort((a, b) => a.price_level - b.price_level || (b.rating_avg || 0) - (a.rating_avg || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        break;
      case 'value':
        filtered.sort((a, b) => getValueScore(b) - getValueScore(a));
        break;
      case 'reviews':
        filtered.sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return filtered;
  }, [pricingData, searchQuery, filterTier, sortBy]);

  const toggleCard = (name) => {
    setExpandedCards(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleAll = () => {
    const allExpanded = filteredBusinesses.every(b => expandedCards[b.name]);
    const newState = {};
    filteredBusinesses.forEach(b => { newState[b.name] = !allExpanded; });
    setExpandedCards(newState);
  };

  const handleTierClick = (level) => {
    const levelStr = String(level);
    setFilterTier(prev => prev === levelStr ? 'all' : levelStr);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px" direction="column">
        <Spinner size="xl" color="green.500" thickness="4px" speed="0.65s" />
        <Text mt={4} color={muted} fontWeight="500">
          Analyzing market pricing...
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="400px" direction="column">
        <Icon as={FaExclamationTriangle} color="red.400" boxSize={10} mb={3} />
        <Text color="red.500" fontSize="lg" fontWeight="700">Error Loading Pricing Data</Text>
        <Text color={muted} mt={2} fontSize="sm">{error}</Text>
      </Flex>
    );
  }

  const ms = pricingData?.market_summary;

  return (
    <Box>
      <AnimatedBox animation="fadeIn" delay={0} duration={0.4}>
        <VStack spacing={1} mb={5}>
          {!demoMode && <SectionLabel description="How businesses in your market are positioned by price, value, and customer perception.">
            Pricing Intelligence
          </SectionLabel>}
        </VStack>
      </AnimatedBox>

      {/* Top 3 podiums */}
      <AnimatedBox animation="fadeIn" delay={0.08} duration={0.4}>
        <Box mb={5}>
          <PricingPodiums businesses={pricingData?.businesses} />
        </Box>
      </AnimatedBox>

      {/* Tier breakdown */}
      <AnimatedBox animation="fadeIn" delay={0.16} duration={0.4}>
        <Box mb={5}>
          <TierSpectrum
            tierDistribution={ms?.tier_distribution}
            totalBusinesses={ms?.total_businesses || 0}
            onTierClick={handleTierClick}
            activeTier={filterTier !== 'all' ? filterTier : null}
            avgRatingByTier={ms?.avg_rating_by_tier}
            avgReviewsByTier={ms?.avg_reviews_by_tier}
          />
        </Box>
      </AnimatedBox>

      {/* Value chart */}
      <AnimatedBox animation="fadeIn" delay={0.24} duration={0.4}>
        <Box mb={5}>
          <ValueMatrix businesses={pricingData?.businesses} />
        </Box>
      </AnimatedBox>

      {/* Filters */}
      <AnimatedBox animation="fadeIn" delay={0.3} duration={0.3}>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" mb={3}>
          <CardBody py={2.5} px={4}>
            <Flex gap={2} flexWrap="wrap" align="center">
              <InputGroup maxW="220px" size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" boxSize={3} />
                </InputLeftElement>
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="lg"
                  fontSize="sm"
                />
              </InputGroup>

              <Select
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                maxW="140px"
                size="sm"
                borderRadius="lg"
              >
                <option value="all">All Tiers</option>
                <option value="4">$$$$ Premium</option>
                <option value="3">$$$ Upscale</option>
                <option value="2">$$ Moderate</option>
                <option value="1">$ Budget</option>
              </Select>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                maxW="180px"
                size="sm"
                borderRadius="lg"
              >
                <option value="price_desc">Price: High → Low</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="value">Best Value</option>
                <option value="rating">Highest Rated</option>
                <option value="reviews">Most Reviewed</option>
                <option value="name">Name A-Z</option>
              </Select>

              <Button
                variant="ghost"
                size="sm"
                borderRadius="lg"
                onClick={toggleAll}
                fontSize="xs"
                color={muted}
                leftIcon={<Icon as={Object.values(expandedCards).some(v => v) ? FaChevronUp : FaChevronDown} boxSize={2.5} />}
              >
                {Object.values(expandedCards).some(v => v) ? 'Collapse' : 'Expand'}
              </Button>

              <Text fontSize="2xs" color={muted} ml="auto">
                {filteredBusinesses.length} / {pricingData?.businesses?.length || 0}
              </Text>
            </Flex>
          </CardBody>
        </Card>
      </AnimatedBox>

      {/* Business list */}
      <VStack spacing={0} align="stretch">
        {filteredBusinesses.map((business, index) => (
          <BusinessRow
            key={business.maps_uri || business.name}
            business={business}
            rank={index + 1}
            isExpanded={expandedCards[business.name]}
            onToggle={() => toggleCard(business.name)}
          />
        ))}
      </VStack>

      {filteredBusinesses.length === 0 && (
        <Flex justify="center" align="center" py={12}>
          <VStack spacing={2}>
            <Icon as={FaSearch} color="gray.300" boxSize={8} />
            <Text color={muted} fontSize="sm">No businesses match your filters</Text>
            <Button
              variant="outline"
              size="xs"
              onClick={() => { setSearchQuery(''); setFilterTier('all'); }}
            >
              Clear Filters
            </Button>
          </VStack>
        </Flex>
      )}

      <MethodologyNote />
    </Box>
  );
};

export default PricingDashboard;
